var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { debug } from '../debug';
import { IDENTITIES_KEYS, } from '../../types';
import { EVENT_NAME, ID5_URL, TIMEOUT } from '../consts';
import { isTopLevel } from '../frames';
import { getHostname, loadScript } from '../utils';
let _instance = null;
const servicesDisallowed = ['email.seznam.cz'];
const services = [
    'horoskopy.cz',
    'novinky.cz',
    'super.cz',
    'hry.seznam.cz',
    'prozeny.cz',
    'seznamzpravy.cz',
    'garaz.cz',
    'sport.cz',
];
/**
 * On some services we are certain we do not want to run ID5
 */
export const _isBlacklistedService = () => {
    return servicesDisallowed.includes(getHostname());
};
/**
 * On some services (internal) we are certain we can run ID5
 */
export const _isWhitelistedService = () => {
    var _a, _b;
    // debug / testing purposes
    if ((_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.href) === null || _b === void 0 ? void 0 : _b.includes('allow_hostname_for_id5=1')) {
        return true;
    }
    return services.includes(getHostname());
};
/**
 * Is WHITELISTED service or service with SSP script (partner)? Go ahead right away
 */
export const _isValidService = () => {
    // TODO: consider SUL metadata as well
    return !_isBlacklistedService() && (_isWhitelistedService() || !!window.sssp);
};
/**
 * Run whole ID5 flow only in some environments (one time check at beginning)
 */
export const _isValidEnvironment = () => {
    return isTopLevel;
};
/**
 * Create instance (singleton with SEZNAM specific partnerId)
 */
export const _getInstance = (handler) => {
    if (!_instance && window.ID5) {
        try {
            _instance = window.ID5.init({ partnerId: 265, cmpApi: 'iab' });
            _instance.onAvailable(handler, TIMEOUT);
            _instance.onUpdate(handler, TIMEOUT);
        }
        catch (_a) {
            debug({ type: 'error', message: 'ID5 instance creation failed' });
        }
    }
};
/**
 * We can generate / fetch ID5 and store it in IS only "with consent" (used multiple times during LOGIN lifecycle updates)
 */
export const _canPropagateID5ToIS = (getter) => {
    var _a;
    const consent = getter(IDENTITIES_KEYS.consent);
    return (_isValidService() &&
        (
        // consented
        (_a = consent === null || consent === void 0 ? void 0 : consent.value) === null || _a === void 0 ? void 0 : _a.purposeOne));
};
/**
 * Fetch ID5 identificator (if not locally stored -> make api call)
 */
export const fetchId5 = (updater, getter) => {
    // hard stop for some environments
    if (!_isValidEnvironment()) {
        return;
    }
    // create instance + fetch (if "login, consent, ..." conditions met)
    const handler = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!_canPropagateID5ToIS(getter)) {
            return;
        }
        try {
            if (!window.ID5) {
                yield loadScript(ID5_URL, 'id5');
            }
        }
        catch (_a) {
            debug({ type: 'error', message: 'ID5 script not loaded' });
            return;
        }
        const ID5handler = (status) => __awaiter(void 0, void 0, void 0, function* () {
            // anxious check (maybe login, consent, ... conditions changed in meantime)
            if (!(yield _canPropagateID5ToIS(getter))) {
                return;
            }
            const newId5 = status.getUserId();
            if (newId5) {
                updater({ id5: { value: newId5 } });
            }
        });
        _getInstance(ID5handler);
    });
    handler();
    // listen for login / consent changes
    window.addEventListener(EVENT_NAME, ((e) => {
        const { changes } = e.detail;
        if (changes.includes(IDENTITIES_KEYS.login) || changes.includes(IDENTITIES_KEYS.consent)) {
            handler();
        }
    }));
    // listen for SSSP being loaded
    if (!_isBlacklistedService() && !_isWhitelistedService()) {
        window.addEventListener('ssploaded', handler);
    }
};
